import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, ConfirmationModal } from '@/components/ui';
import { documentService } from '@/services/documentService';
import toast from 'react-hot-toast';
import { formatDocDate, getFileIconType } from '../utils/documentHelpers';
import { FileSpreadsheet, FileText, FileType, FolderOpen } from 'lucide-react';

const FILE_ICONS = {
  excel: FileSpreadsheet,
  word: FileText,
  pdf: FileType,
  other: FolderOpen,
};

export const DocumentModals = ({
  showCreateModal,
  setShowCreateModal,
  createType,
  createTitle,
  setCreateTitle,
  creating,
  handleCreateBlank,
  showUploadModal,
  setShowUploadModal,
  uploadFile,
  setUploadFile,
  uploadTitle,
  setUploadTitle,
  uploadCategory,
  setUploadCategory,
  isRelatedToCatalog,
  setIsRelatedToCatalog,
  uploadNotes,
  setUploadNotes,
  handleUpload,
  uploading,
  setCreateType,
  showPreviewModal,
  setShowPreviewModal,
  selectedDoc,
  editTitle,
  setEditTitle,
  handleUpdate,
  editFile,
  setEditFile,
  excelData,
  canEdit,
  user,
  apiBaseUrl,
  showPermissionModal,
  setShowPermissionModal,
  allUsers,
  handleAddUser,
  handleUpdatePermissions,
  fetchDocuments,
  setSelectedDoc,
  showDeleteModal,
  setShowDeleteModal,
  confirmDelete,
  docToDeleteId,
  setDocToDeleteId,
  deleting,
}) => {
  const uploadInputRef = useRef(null);
  const editInputRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    const fetchPreview = async () => {
      if (showPreviewModal && selectedDoc && (selectedDoc.fileType?.includes('pdf') || selectedDoc.fileType?.includes('image'))) {
        try {
          const url = `${apiBaseUrl}${selectedDoc.fileUrl}`;
          const token = localStorage.getItem('company_token') || localStorage.getItem('token') || '';
          const response = await fetch(url, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          if (response.ok) {
            const rawBlob = await response.blob();
            let blobType = rawBlob.type;
            if (selectedDoc.fileType?.includes('pdf') || selectedDoc.fileName?.endsWith('.pdf')) {
              blobType = 'application/pdf';
            }
            const blob = new Blob([rawBlob], { type: blobType });
            objectUrl = window.URL.createObjectURL(blob);
            setPreviewUrl(objectUrl);
          }
        } catch (error) {
          console.error('Failed to fetch preview', error);
        }
      }
    };
    fetchPreview();
    return () => {
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
      setPreviewUrl(null);
    };
  }, [showPreviewModal, selectedDoc, apiBaseUrl]);

  const DocIcon = selectedDoc ? FILE_ICONS[getFileIconType(selectedDoc)] || FolderOpen : FolderOpen;

  return (
    <>
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={`Create New ${createType === 'excel' ? 'Excel' : 'Word'}`}
        size="md"
      >
        <form onSubmit={handleCreateBlank} className="space-y-4">
          <div>
            <label className="label">Document Title</label>
            <input
              className="input-field"
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              placeholder={`e.g. New ${createType === 'excel' ? 'Spreadsheet' : 'Document'}`}
              required
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-2">
              Generated Filename: {createTitle ? createTitle.replace(/\s+/g, '_') : 'filename'}_{
                (() => {
                  const d = new Date();
                  const pad = (n) => n.toString().padStart(2, '0');
                  return `${pad(d.getDate())}${pad(d.getMonth() + 1)}${d.getFullYear()}_${pad(d.getHours())}${pad(d.getMinutes())}`;
                })()
              }.{createType === 'excel' ? 'xlsx' : 'docx'}
            </p>
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
            <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating || !createTitle}>
              {creating ? 'Creating...' : 'Create Document'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadFile(null);
          setUploadTitle('');
          if (setUploadCategory) setUploadCategory('');
          if (setIsRelatedToCatalog) setIsRelatedToCatalog(false);
          if (setUploadNotes) setUploadNotes('');
        }}
        title="Add Document"
        size="md"
      >
        <div className="space-y-3">
          <div>
            <label className="label mb-2 text-xs">Create New Blank Document</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setCreateType('excel');
                  setShowCreateModal(true);
                  setShowUploadModal(false);
                }}
                className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 border-slate-100 hover:border-emerald-500 hover:bg-emerald-50 transition-all group"
              >
                <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileSpreadsheet className="w-5 h-5" />
                </div>
                <span className="text-[10px] font-bold text-slate-700">Blank Excel</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreateType('word');
                  setShowCreateModal(true);
                  setShowUploadModal(false);
                }}
                className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 transition-all group"
              >
                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileText className="w-5 h-5" />
                </div>
                <span className="text-[10px] font-bold text-slate-700">Blank Word</span>
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-100" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase">
              <span className="bg-white px-2 text-slate-400 font-bold">Or upload existing file</span>
            </div>
          </div>
          
          {/* Developer : Nithin
              Date : 28-06-2026
              changes: Added Is Related to Catalog checkbox to route uploads to specific folder */}
          <div className="flex items-center gap-2 px-1">
            <input 
              type="checkbox" 
              id="relatedToCatalog" 
              checked={isRelatedToCatalog || false} 
              onChange={(e) => {
                setIsRelatedToCatalog(e.target.checked);
                if (e.target.checked) {
                  if (setUploadCategory) setUploadCategory('Estimation');
                }
              }} 
              className="w-4 h-4 text-brand-blue rounded border-slate-300 focus:ring-brand-blue" 
            />
            <label htmlFor="relatedToCatalog" className="text-xs text-slate-600 font-medium cursor-pointer">
              Is Related to Catalog
            </label>
          </div>

          <form onSubmit={handleUpload} className="space-y-2.5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label text-[11px] mb-1">Document Title</label>
                <input
                  className="input-field py-1.5 text-xs"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="e.g. Q4 Financial Report"
                />
              </div>
              <div>
                <label className="label text-[11px] mb-1">
                  Category {isRelatedToCatalog && <span className="text-red-500">*</span>}
                </label>
                {isRelatedToCatalog ? (
                  <select
                    className="input-field py-1.5 text-xs bg-white"
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    required
                  >
                    <option value="Estimation">Estimation</option>
                  </select>
                ) : (
                  <input
                    className="input-field py-1.5 text-xs"
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    placeholder="e.g. Finance, Contract, etc."
                  />
                )}
              </div>
            </div>
            <div>
              <label className="label text-[11px] mb-1">Notes</label>
              <textarea
                className="input-field py-1.5 text-xs min-h-[50px] resize-none"
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Add any additional notes here..."
              />
            </div>
            <div>
              <label className="label text-[11px] mb-1">Select File *</label>
              <button
                type="button"
                onClick={() => uploadInputRef.current?.click()}
                className="w-full flex justify-center px-4 py-3 border-2 border-slate-200 border-dashed rounded-xl hover:border-slate-800 transition-colors cursor-pointer bg-slate-50/50"
              >
                <span className="text-xs font-bold text-slate-600">
                  {uploadFile ? uploadFile.name : 'Click to select file'}
                </span>
              </button>
              <input
                ref={uploadInputRef}
                type="file"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setUploadFile(file);
                  if (file && !uploadTitle) setUploadTitle(file.name.split('.')[0]);
                }}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button type="button" variant="secondary" className="py-1.5 text-xs" onClick={() => {
                setShowUploadModal(false);
                setUploadFile(null);
                setUploadTitle('');
                if (setUploadCategory) setUploadCategory('');
                if (setUploadNotes) setUploadNotes('');
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={!uploadFile || uploading} className="py-1.5 text-xs flex items-center gap-2">
                {uploading && <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {uploading ? 'Uploading...' : 'Start Upload'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {selectedDoc && (
        <Modal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          title={selectedDoc.title}
          size="xl"
        >
          <div className="flex flex-col h-[80vh]">
            <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50/50 rounded-t-lg">
              <div className="flex items-center gap-2">
                <DocIcon className="w-5 h-5 text-slate-600" />
                {user?.role === 'owner' ? (
                  <input
                    className="bg-transparent border-none font-semibold text-slate-800 focus:ring-0 p-0 text-sm hover:bg-white px-2 py-1 rounded"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onBlur={handleUpdate}
                  />
                ) : (
                  <span className="font-semibold text-slate-800 text-sm">{selectedDoc.title}</span>
                )}
              </div>
              <a
                href={previewUrl || `${apiBaseUrl}${selectedDoc.fileUrl}`}
                download={selectedDoc.fileName || selectedDoc.title}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold text-white bg-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-800"
              >
                Download
              </a>
            </div>
            <div className="flex-1 bg-slate-100 overflow-hidden">
              {excelData ? (
                <div className="flex flex-col h-full">
                  {canEdit(selectedDoc) && (
                    <div className="bg-amber-50 p-2 flex justify-end border-b border-amber-100">
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            const table = document.getElementById('excel-preview-table');
                            const wb = window.XLSX.utils.table_to_book(table);
                            const wbout = window.XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                            const blob = new Blob([wbout], {
                              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            });
                            const file = new File([blob], selectedDoc.fileName, { type: blob.type });
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('title', editTitle);
                            const updated = await documentService.updateDocument(selectedDoc._id, formData);
                            if (updated.success) setSelectedDoc(updated.data);
                            toast.success('Excel changes saved');
                            setShowPreviewModal(false);
                            fetchDocuments();
                          } catch {
                            toast.error('Failed to save Excel changes');
                          }
                        }}
                      >
                        Save Changes
                      </Button>
                    </div>
                  )}
                  <div className="flex-1 overflow-auto p-4 bg-white">
                    <table id="excel-preview-table" className="min-w-full text-[13px] border border-slate-200">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          {excelData[0]?.map((cell, i) => (
                            <th key={i} className="px-3 py-2 text-left font-semibold border-b border-r border-slate-200">
                              {cell || `Col ${i + 1}`}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {excelData.slice(1).map((row, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            {row.map((cell, j) => (
                              <td
                                key={j}
                                contentEditable={canEdit(selectedDoc)}
                                className="px-3 py-2 border-r border-slate-100 outline-none focus:bg-amber-50"
                              >
                                {cell?.toString() || ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : selectedDoc.fileType?.includes('pdf') || selectedDoc.fileType?.includes('image') ? (
                previewUrl ? (
                  <iframe
                    src={previewUrl}
                    className="w-full h-full border-none"
                    title="Document Preview"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500 text-sm">
                    Loading preview...
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-10 text-center">
                  <DocIcon className="w-12 h-12 text-slate-400 mb-4" />
                  <p className="text-sm text-slate-500 max-w-sm">
                    Download this file to view and edit it locally.
                  </p>
                  <a
                    href={`${apiBaseUrl}${selectedDoc.fileUrl}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 text-sm font-semibold text-brand-blue hover:underline"
                  >
                    Download & Open
                  </a>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {selectedDoc && (
        <Modal isOpen={showPermissionModal} onClose={() => setShowPermissionModal(false)} title="Manage Access" size="md">
          <div className="space-y-6">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center border border-slate-50">
                <DocIcon className="w-6 h-6 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-slate-800 truncate">{selectedDoc.title}</h4>
                <p className="text-[10px] text-slate-400 truncate">
                  {selectedDoc.fileName} • {formatDocDate(selectedDoc.createdAt)}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-semibold text-slate-800">General Access</h5>
                <p className="text-[11px] text-slate-400">Anyone with the link can view</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={selectedDoc.isPublic}
                  onChange={async () => {
                    const res = await handleUpdatePermissions({
                      isPublic: !selectedDoc.isPublic,
                    });
                    if (res?.success) {
                      setSelectedDoc({ ...selectedDoc, isPublic: !selectedDoc.isPublic });
                      fetchDocuments();
                    }
                  }}
                />
                <div className="w-10 h-5 bg-slate-200 rounded-full peer peer-checked:bg-amber-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5" />
              </label>
            </div>

            <div className="space-y-3 max-h-64 overflow-y-auto custom-scrollbar">
              <h5 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">People with access</h5>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {selectedDoc.uploadedBy?.name?.charAt(0) || 'O'}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-800">{selectedDoc.uploadedBy?.name || 'Owner'}</div>
                </div>
                <span className="text-[10px] font-bold text-slate-500 uppercase px-2 py-1 bg-slate-100 rounded">Owner</span>
              </div>
              {selectedDoc.permissions?.map((p, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-slate-100 text-slate-600 rounded-full flex items-center justify-center text-xs font-bold">
                    {p.user?.name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{p.user?.name}</div>
                  </div>
                  <select
                    className="text-[10px] font-bold uppercase bg-slate-50 rounded py-1 px-2"
                    value={p.canEdit ? 'editor' : 'viewer'}
                    onChange={async (e) => {
                      const newPerms = selectedDoc.permissions.map((perm, i) =>
                        i === idx ? { ...perm, canEdit: e.target.value === 'editor' } : perm
                      );
                      const res = await handleUpdatePermissions({
                        permissions: newPerms,
                      });
                      if (res?.success) {
                        setSelectedDoc({ ...selectedDoc, permissions: res.data.permissions });
                        fetchDocuments();
                      }
                    }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </select>
                  <button
                    type="button"
                    onClick={async () => {
                      const newPerms = selectedDoc.permissions.filter((_, i) => i !== idx);
                      const res = await handleUpdatePermissions({
                        permissions: newPerms,
                      });
                      if (res?.success) {
                        setSelectedDoc({ ...selectedDoc, permissions: res.data.permissions });
                        fetchDocuments();
                      }
                    }}
                    className="p-1 text-slate-300 hover:text-red-500"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <select
              className="input-field text-xs"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) {
                  handleAddUser(e.target.value);
                  e.target.value = '';
                }
              }}
            >
              <option value="">Add people...</option>
              {allUsers
                .filter(
                  (u) =>
                    u.status === 'Active' &&
                    u.role !== 'owner' &&
                    !selectedDoc.permissions?.some((p) => (p.user?._id || p.user) === u._id)
                )
                .map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name} ({u.role})
                  </option>
                ))}
            </select>

            <Button className="w-full" onClick={() => setShowPermissionModal(false)}>
              Done
            </Button>
          </div>
        </Modal>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDocToDeleteId(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </>
  );
};




